import React from 'react';
import { isEmpty } from 'lodash';
import { THEME_COLOR_SWATCH } from 'utils/constants';
import { formatYAxisLabel } from './utils'

export const mapTooltipColors = (chartData, chartConfig) => {
  const mappedColors = (chartData.uniqueGroupValues || []).map((item, index) => {
    let displayName = item
    if (typeof item === 'string' && item.includes('_stream_')) {
      displayName = item.split('_stream_')[0]
    }
    return {
      name: displayName,
      color: getStrokeLine(chartConfig, index)
    }
  })
  return mappedColors
}

export const getStrokeLine = (chartConfig, index) => {
  const streams = chartConfig?.dataStreams?.streams || []
  let color = streams[index]?.streamLabel?.color || ''

  if (!color) {
    const n = THEME_COLOR_SWATCH.length
    const mappedIdx = (index % 2 === 0)
      ? Math.floor(index / 2)
      : (n - 1) - Math.floor((index - 1) / 2)
    color = THEME_COLOR_SWATCH[mappedIdx % n]
  }

  return color
}

export const getFormattedPayload = (payload, format, overrides, decimal, groupByColors, chartConfig) => {

  // Determine if there is any non-time group-by configured. We treat
  // "no group by" as time-only so that tooltips default to metric names
  // instead of SQL aliases like "stream_1".
  const globalGroupByMetrics = Array.isArray(chartConfig?.groupBy?.metrics)
    ? chartConfig.groupBy.metrics
    : []
  const nonTimeGlobalGroupBy = globalGroupByMetrics.filter((m) => !/time/i.test(m))

  const singleGroupByMetrics = Array.isArray(chartConfig?.dataStreamSingle?.groupBy?.metrics)
    ? chartConfig.dataStreamSingle.groupBy.metrics
    : []
  const nonTimeSingleGroupBy = singleGroupByMetrics.filter((m) => !/time/i.test(m))

  const hasNonTimeGroupBy = nonTimeGlobalGroupBy.length > 0 || nonTimeSingleGroupBy.length > 0

  return payload.map((metric, index) => {
    const dataStream = chartConfig?.dataStreams?.streams?.[index];

    // Metric key from configuration (e.g. underlying field name). This is the
    // key used for yDataOverrides, so it must be our primary lookup key.
    const metricNameFromConfig = (Array.isArray(dataStream?.metric?.metrics) && dataStream.metric.metrics.length > 0)
      ? dataStream.metric.metrics[0]
      : ''

    // Base name from Recharts payload or stream label
    let name = metric.name || metric.dataKey || dataStream?.streamLabel?.value || metricNameFromConfig

    // When we truly have a non-time group-by, composite keys look like
    // "groupByValue_stream_1". In that case we want the group-by value, so
    // strip the suffix and keep the prefix.
    if (hasNonTimeGroupBy && typeof name === 'string' && name.includes('_stream_')) {
      const parts = name.split('_stream_')
      if (parts.length > 1 && parts[0]) {
        name = parts[0]
      }
    }

    const metricKey = metricNameFromConfig || metric.name || metric.dataKey || dataStream?.streamLabel?.value || ''

    // When there is NO non-time group-by, label precedence should be:
    // 1) Explicit override from configuration (overrides)
    // 2) Stream label from editor (streamLabel.value)
    // 3) Underlying metric name from configuration
    if (!hasNonTimeGroupBy) {
      let overrideLabel
      if (overrides && overrides[metricKey] !== undefined && overrides[metricKey] !== null) {
        const o = overrides[metricKey]
        overrideLabel = typeof o === 'object' && o.value !== undefined ? o.value : o
      }

      const cleanedOverride =
        typeof overrideLabel === 'string' ? overrideLabel.trim() : overrideLabel

      if (cleanedOverride) {
        name = cleanedOverride
      } else if (dataStream?.streamLabel?.value) {
        name = dataStream.streamLabel.value
      } else if (metricNameFromConfig) {
        name = metricNameFromConfig
      }
    }

    const getColor = groupByColors && groupByColors[metricKey] ? groupByColors[metricKey] : metric.color
    return {
      name: name,
      color: getColor,
      value: formatYAxisLabel(metric.value, format, decimal)
    }
  })
}

export const GroupXAxisTooltip = ({ active, payload, label, format, overrides = {}, decimal, groupByColors, groups = [], chartConfig, mappedColors = [] }) => {

  if (active && payload?.length) {
    const group = payload[0]?.payload?.group || {};
    return (
      <div className="job-training__charts_tootip">
        <div className="job-training__charts_tootip_header">
          <span>Groups</span>
          <span className="job-training__charts_tootip_value">
            {label ? label?.slice(0, 10) :
              isEmpty(groups) ?
                Object.entries(group).map(([key, value]) =>
                  <span key={key}>{value}</span>
                )
                :
                groups.map(key =>
                  <span key={key}>{group[key]}</span>
                )
            }
          </span>
        </div>
        {getFormattedPayload(payload, format, overrides, decimal, groupByColors, chartConfig).map((metric, index) => {
          let color = mappedColors[index]?.color

          return (
            <div key={index} className="job-training__charts_tootip_header job-training__charts_tootip_header--item">
              <span className="label-color">{metric.name}</span>
              <span className='job-training__charts_tootip_value' style={{ color: color }}>{metric.value}</span>
            </div>
          )
        })}
      </div>
    );
  }
  return null;
};

export const TimeSeriesTooltip = ({ active, payload, label, format, overrides = {}, decimal, title, chartDef, groupBy, chartConfig }) => {

  if (active && payload?.length) {
    return (
      <div className="job-training__charts_tootip" >
        <div className="job-training__charts_tootip_header">
          <span>{chartDef?.tooltipTitle || title || 'Date'}</span>
          <span className="job-training__charts_tootip_value">{label ? label.toString().slice(0, 10) : ''}</span>
        </div>

        {getFormattedPayload(payload, format, overrides, decimal, '', chartConfig).map((metric, index) => {
          let color = metric.color

          if (chartDef) {
            const findMetric = chartDef?.metrics.find(def => def.dataKey === metric.name)
            color = findMetric?.fill
          }
          return (
            <div key={index} className="job-training__charts_tootip_header job-training__charts_tootip_header--item">
              <span className="label-color">{metric.name}</span>
              <span className='job-training__charts_tootip_value' style={{ color: color }}>{metric.value}</span>
            </div>
          )
        })}
      </div>
    );
  }
  return null;
};